import {useCallback, useEffect, useRef, useSyncExternalStore} from 'react'
import {sessionStorageStore} from './session-storage-store'
import type {Listener} from './create-store'
import type {ClearKeysOptions, WriteOptions} from './types'

const {
  cache,
  readStorage,
  writeStorage,
  resetStorage,
  subscribe,
  clearKeys,
  clearKeysByPrefix,
  getItemsByPrefix,
  clearAll: clearAllSessionStorage,
} = sessionStorageStore

/**
 * Clears all store-tracked sessionStorage keys, their in-memory cache,
 * and the underlying browser sessionStorage.
 */
export function clearAllSessionStorageCache(): void {
  clearAllSessionStorage()
  if (typeof window !== 'undefined') {
    window.sessionStorage.clear()
  }
}

/**
 * Clears specified keys from sessionStorage.
 * Pass `{silent: true}` to skip notifying subscribers and only remove from browser storage.
 */
export function clearSessionStorage(keys: string[], options?: ClearKeysOptions): void {
  clearKeys(keys, options)
}

/**
 * Get all items whose keys start with a prefix
 */
export function getSessionStorageItemsForKeyPrefix<T = unknown>(prefix: string): T[] {
  return getItemsByPrefix<T>(prefix)
}

/**
 * Clear all keys matching a prefix from sessionStorage
 */
export function clearSessionStorageByKeyPrefix(prefix: string): void {
  clearKeysByPrefix(prefix)
}

/**
 * React hook for sessionStorage with cross-component synchronization.
 * Returns [value, setValue, reset] tuple.
 */
export function useSessionStorage<T>(
  key: string,
  initialValue: T,
): [T, (value: T | ((prev: T) => T), options?: WriteOptions) => void, () => void] {
  // Use ref for initialValue so it's available in callbacks without stale closure issues
  const initialValueRef = useRef(initialValue)
  useEffect(() => {
    initialValueRef.current = initialValue
  })

  // Seed lazily inside getSnapshot (idempotent) instead of during render
  // to avoid mutating external state in the render phase (concurrent-safe).
  // Uses initialValue directly (not ref) because getSnapshot runs during render
  // before the effect that updates initialValueRef.
  const getSnapshot = useCallback((): T => {
    if (!cache.has(key)) {
      readStorage(key, initialValue)
    }
    return cache.get(key) as T
  }, [key, initialValue])

  const getServerSnapshot = useCallback((): T => {
    return initialValue
  }, [initialValue])

  const subscribeToStore = useCallback((listener: Listener) => subscribe(key, listener), [key])

  const value = useSyncExternalStore(subscribeToStore, getSnapshot, getServerSnapshot)

  const setValue = useCallback(
    (next: T | ((prev: T) => T), options?: WriteOptions) => {
      const current = cache.has(key) ? (cache.get(key) as T) : initialValueRef.current
      const resolved = typeof next === 'function' ? (next as (prev: T) => T)(current) : next
      // Treat undefined as a reset to avoid writing "undefined" string to storage
      if (resolved === undefined) {
        resetStorage(key, initialValueRef.current)
      } else {
        writeStorage(key, resolved, options)
      }
    },
    [key],
  )

  const reset = useCallback(() => {
    resetStorage(key, initialValueRef.current)
  }, [key])

  return [value, setValue, reset] as const
}
